import React, { useCallback, useEffect, useState } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  Button,
  Card,
  Form,
  Header,
  Loader,
  Modal,
  Table,
} from 'semantic-ui-react';
import { API, showError } from '../../helpers';
import { renderNumber, renderWan, renderYuan } from '../../helpers/render';
import './OrgRanking.css';

// 组织 Token 使用量/增长排名。数据来自 Doris 物化视图，经 /api/dashboard/ranking 取数（仅管理员）。

const fmtDate = (d) => {
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
};

const lastSevenDays = () => {
  const now = new Date();
  return [fmtDate(new Date(now.getTime() - 6 * 86400000)), fmtDate(now)];
};

const fmtGrowth = (v) => {
  if (v === null || v === undefined) return '新增';
  return (v >= 0 ? '+' : '') + v.toFixed(1) + '%';
};

const growthClass = (v) => {
  if (v === null || v === undefined) return 'org-rank-new';
  if (v > 0) return 'org-rank-up';
  if (v < 0) return 'org-rank-down';
  return 'org-rank-flat';
};

const DIM_OPTIONS = [
  { key: 'company', label: '公司' },
  { key: 'department', label: '部门' },
  { key: 'project', label: '项目' },
];

const OrgRanking = () => {
  const [range, setRange] = useState(lastSevenDays);
  const [dimension, setDimension] = useState('company');
  const [sortMode, setSortMode] = useState('usage');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drill, setDrill] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await API.get('/api/dashboard/ranking', {
        params: { t1: range[0], t2: range[1], dimension, sort: sortMode, limit: 50 },
      });
      if (res.data.success) setRows(res.data.data || []);
      else showError(res.data.message);
    } catch (e) {
      showError(e);
    }
    setLoading(false);
  }, [range, dimension, sortMode]);

  useEffect(() => {
    load();
  }, [load]);

  // 「未分配」(org_id = 0) 与「未归属」(org_id = -1) 两个桶都不是真实组织：
  //   0  = 令牌在、但该维度没选组织 → 业务方补归属
  //   -1 = 令牌在 tokens 表里查不到、且没有改名留档 → 人工确认（删除 or 改名）
  // 它们的可执行动作是「找出具体哪些令牌」，故下钻直接列令牌清单，而不是模型分布。
  const UNASSIGNED_ORG_ID = 0;
  const UNATTRIBUTED_ORG_ID = -1;

  const openDrill = async (row) => {
    const isBucket = row.org_id === UNASSIGNED_ORG_ID || row.org_id === UNATTRIBUTED_ORG_ID;
    setDrill({
      open: true,
      orgName: row.org_name,
      kind: isBucket ? 'tokens' : 'model',
      reason: row.org_id === UNATTRIBUTED_ORG_ID ? 'token_missing' : 'org_missing',
      data: [],
      loading: true,
    });
    try {
      const url = isBucket ? '/api/dashboard/unattributed' : '/api/dashboard/org-model';
      const params = isBucket
        ? { t1: range[0], t2: range[1], dimension }
        : { t1: range[0], t2: range[1], dimension, id: row.org_id };
      const res = await API.get(url, { params });
      if (res.data.success) {
        setDrill((d) => ({ ...d, data: res.data.data || [], loading: false }));
      } else {
        showError(res.data.message);
        setDrill((d) => ({ ...d, loading: false }));
      }
    } catch (e) {
      showError(e);
      setDrill((d) => ({ ...d, loading: false }));
    }
  };

  const parentLabel = (r) => {
    if (dimension === 'department') return r.company_name || '-';
    if (dimension === 'project') {
      const parts = [r.company_name, r.department_name].filter(Boolean);
      return parts.length ? parts.join(' / ') : '-';
    }
    return '';
  };

  const colCount = dimension === 'company' ? 10 : 11;

  // 令牌清单按「原因」过滤：同一个接口会同时返回「未分配」与「未归属」两类，
  // 点击哪一行就只看哪一类。
  const drillTokens =
    drill && drill.kind === 'tokens'
      ? (drill.data || []).filter((t) => t.reason === drill.reason)
      : [];

  return (
    <div className='dashboard-container'>
      <Card fluid className='chart-card'>
        <Card.Content>
          <Header as='h3'>组织 Token 使用排名</Header>
          <div className='org-ranking-toolbar'>
            <Form.Input
              type='date'
              label='开始日期'
              value={range[0]}
              onChange={(e) => setRange([e.target.value, range[1]])}
            />
            <Form.Input
              type='date'
              label='结束日期'
              value={range[1]}
              onChange={(e) => setRange([range[0], e.target.value])}
            />
            <div>
              <label>维度</label>
              <Button.Group size='mini'>
                {DIM_OPTIONS.map((o) => (
                  <Button
                    key={o.key}
                    toggle
                    active={dimension === o.key}
                    onClick={() => setDimension(o.key)}
                  >
                    {o.label}
                  </Button>
                ))}
              </Button.Group>
            </div>
            <div>
              <label>排名方式</label>
              <Button.Group size='mini'>
                <Button toggle active={sortMode === 'usage'} onClick={() => setSortMode('usage')}>
                  使用量
                </Button>
                <Button toggle active={sortMode === 'growth'} onClick={() => setSortMode('growth')}>
                  增长率
                </Button>
              </Button.Group>
            </div>
          </div>
        </Card.Content>
      </Card>

      {loading ? (
        <Loader active>加载中...</Loader>
      ) : (
        <Card fluid className='chart-card'>
          <Card.Content>
            <div className='org-ranking-table-wrap'>
              <Table celled selectable compact>
                <Table.Header>
                  <Table.Row>
                    <Table.HeaderCell>#</Table.HeaderCell>
                    <Table.HeaderCell>名称</Table.HeaderCell>
                    {dimension !== 'company' && <Table.HeaderCell>所属</Table.HeaderCell>}
                    <Table.HeaderCell>Token (万)</Table.HeaderCell>
                    <Table.HeaderCell>环比增长</Table.HeaderCell>
                    <Table.HeaderCell>额度 (¥)</Table.HeaderCell>
                    <Table.HeaderCell>请求</Table.HeaderCell>
                    <Table.HeaderCell>平均延迟 (ms)</Table.HeaderCell>
                    <Table.HeaderCell>活跃令牌</Table.HeaderCell>
                    <Table.HeaderCell>模型数</Table.HeaderCell>
                    <Table.HeaderCell>流式占比</Table.HeaderCell>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {rows.length === 0 ? (
                    <Table.Row>
                      <Table.Cell colSpan={colCount} textAlign='center'>
                        暂无数据
                      </Table.Cell>
                    </Table.Row>
                  ) : (
                    rows.map((r, i) => (
                      <Table.Row key={r.org_id} onClick={() => openDrill(r)} style={{ cursor: 'pointer' }}>
                        <Table.Cell>{i + 1}</Table.Cell>
                        <Table.Cell>{r.org_name || '-'}</Table.Cell>
                        {dimension !== 'company' && <Table.Cell>{parentLabel(r)}</Table.Cell>}
                        <Table.Cell>{renderWan(r.cur_tokens)}</Table.Cell>
                        <Table.Cell className={growthClass(r.token_growth_rate)}>
                          {fmtGrowth(r.token_growth_rate)}
                        </Table.Cell>
                        <Table.Cell>{renderYuan(r.cur_quota)}</Table.Cell>
                        <Table.Cell>{renderNumber(r.cur_requests)}</Table.Cell>
                        <Table.Cell>{r.avg_latency ? Math.round(r.avg_latency) : '-'}</Table.Cell>
                        <Table.Cell>{r.active_tokens}</Table.Cell>
                        <Table.Cell>{r.model_count}</Table.Cell>
                        <Table.Cell>{(r.stream_ratio * 100).toFixed(0)}%</Table.Cell>
                      </Table.Row>
                    ))
                  )}
                </Table.Body>
              </Table>
            </div>
          </Card.Content>
        </Card>
      )}

      <Modal open={!!drill && drill.open} onClose={() => setDrill(null)} size='small'>
        <Modal.Header>
          {drill
            ? drill.kind === 'tokens'
              ? `${drill.orgName} · 令牌清单`
              : `${drill.orgName} · 模型分布`
            : ''}
        </Modal.Header>
        <Modal.Content>
          {drill && drill.loading ? (
            <Loader active>加载中...</Loader>
          ) : drill && drill.kind === 'tokens' ? (
            drillTokens.length > 0 ? (
              <Table celled compact>
                <Table.Header>
                  <Table.Row>
                    <Table.HeaderCell>令牌名</Table.HeaderCell>
                    <Table.HeaderCell>用户 ID</Table.HeaderCell>
                    <Table.HeaderCell>Token (万)</Table.HeaderCell>
                    <Table.HeaderCell>额度 (¥)</Table.HeaderCell>
                    <Table.HeaderCell>请求</Table.HeaderCell>
                    <Table.HeaderCell>用量区间</Table.HeaderCell>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {drillTokens.map((t) => (
                    <Table.Row key={`${t.user_id}-${t.token_name}`}>
                      <Table.Cell>{t.token_name}</Table.Cell>
                      <Table.Cell>{t.user_id}</Table.Cell>
                      <Table.Cell>{renderWan(t.tokens)}</Table.Cell>
                      <Table.Cell>{renderYuan(t.quota)}</Table.Cell>
                      <Table.Cell>{renderNumber(t.requests)}</Table.Cell>
                      <Table.Cell>
                        {t.first_dt} ~ {t.last_dt}
                      </Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table>
            ) : (
              <div style={{ textAlign: 'center', padding: 40 }}>暂无数据</div>
            )
          ) : drill && drill.data.length > 0 ? (
            <ResponsiveContainer width='100%' height={Math.max(360, drill.data.length * 30)}>
              <BarChart
                data={drill.data}
                layout='vertical'
                margin={{ top: 8, right: 24, bottom: 8, left: 24 }}
              >
                <CartesianGrid strokeDasharray='3 3' />
                <XAxis type='number' tick={{ fontSize: 11 }} />
                <YAxis type='category' dataKey='model_name' width={150} tick={{ fontSize: 11 }} />
                <Tooltip formatter={(v) => renderWan(v) + ' 万'} />
                <Bar dataKey='tokens' fill='#8884d8' name='Token (万)' />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div style={{ textAlign: 'center', padding: 40 }}>暂无数据</div>
          )}
        </Modal.Content>
      </Modal>
    </div>
  );
};

export default OrgRanking;
